import { supabase } from '@/integrations/supabase/client';
import { agendaOwnerSession, agendaUuid, hasConfirmedAgendaReceiptResult, invokeAgendaAction,
  isConfirmedAgendaReceiptFailure, mirrorAgendaReceiptReference, readPendingAgendaAction } from '@/lib/agendaExecutor';
import { finishConnectorWrite, type PendingConnectorWrite } from '@/lib/connectorPendingWrite';
import { confirmMirrorAgendaReceipt } from '@/lib/confirmMirrorAgendaReceipt';
import { mirrorAgendaRecoveryLocator } from '@/lib/agendaRecovery';

interface Options {
  ownerId: string;
  operationId: string;
  recoveryOnly?: boolean;
  language: string;
  isCurrent: () => boolean;
}

/**
 * A previous durable journal is not the new proposal. Resolve only its exact
 * owned receipt, acknowledge its ORIGINAL conversation, then release that
 * journal. Never replay its mutation or attach its result to the current turn.
 */
export async function resolvePreviousAgendaWrite(options: Options): Promise<boolean> {
  const { ownerId, operationId, language, isCurrent } = options;
  const assert = () => { if (!isCurrent()) throw new Error('invalid_session'); };
  assert();
  if (!agendaUuid(ownerId) || !agendaUuid(operationId)) return false;
  if (options.recoveryOnly) return true;
  const pending = await readPendingAgendaAction(ownerId); assert();
  if (!pending || pending.body.operation_id === operationId) return true;
  const entry = structuredClone(pending);
  if (entry.ownerId !== ownerId || entry.group !== 'local_agenda' || entry.key !== ownerId + ':local_agenda') return false;
  const originalJournal = async () => {
    assert(); await agendaOwnerSession(ownerId, assert);
    const latest = await readPendingAgendaAction(ownerId); assert();
    if (!latest || latest.ownerId !== entry.ownerId || latest.group !== 'local_agenda'
      || latest.key !== entry.key || JSON.stringify(latest.body) !== JSON.stringify(entry.body)) {
      throw new Error('agenda_changed');
    }
  };
  await originalJournal();
  const context = entry.body.mirror_context;
  const locator = context ? mirrorAgendaRecoveryLocator(entry) : null;
  if (context && !locator) return false;
  const result = await invokeAgendaAction({
    action: 'receipt', operation_id: String(entry.body.operation_id),
    ...(context ? { mirror_context: context as { session_id: string; action_id: string } } : {}),
  }, ownerId, assert);
  await originalJournal();
  if (isConfirmedAgendaReceiptFailure(entry.body, result)) {
    await finishConnectorWrite(entry, true, assert); assert();
    return !await readPendingAgendaAction(ownerId) && isCurrent();
  }
  if (!hasConfirmedAgendaReceiptResult(entry.body, result, ownerId)) return false;
  if (locator) {
    const confirmed: PendingConnectorWrite = { ...entry, status: 'succeeded' };
    const receipt = mirrorAgendaReceiptReference(confirmed, {
      userId: locator.user_id, sessionId: locator.session_id, actionId: locator.action_id,
    });
    if (!receipt) return false;
    const message = await confirmMirrorAgendaReceipt({ client: supabase, receipt, language, isCurrent });
    if (!message) return false;
    await originalJournal();
  }
  await agendaOwnerSession(ownerId, assert); assert();
  await finishConnectorWrite(entry, true, assert); assert();
  const remaining = await readPendingAgendaAction(ownerId); assert();
  return !remaining;
}
